"use client";

import { useEffect, useRef } from "react";
import {
  CONTACT_FORM_HASH_HREF,
  TESTIMONIALS_HASH_HREF,
} from "@/lib/contact-form";

/** In-page hash targets (Lenis na desktopie wyłącza natywny hash scroll). */
const PAGE_HASHES = [CONTACT_FORM_HASH_HREF, TESTIMONIALS_HASH_HREF] as const;

const SCROLL_OFFSET = 0;
const SCROLL_DURATION = 1.25;

type LenisLike = {
  scroll: number;
  scrollTo: (
    target: number,
    opts?: { duration?: number; force?: boolean },
  ) => void;
};

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function normalizeHash(href: string): string | null {
  const trimmed = href.trim();
  if (trimmed.startsWith("#")) {
    return trimmed;
  }
  try {
    const url = new URL(trimmed, window.location.origin);
    if (
      url.pathname === window.location.pathname &&
      url.hash &&
      PAGE_HASHES.includes(url.hash as (typeof PAGE_HASHES)[number])
    ) {
      return url.hash;
    }
  } catch {
    /* ignore invalid URLs */
  }
  for (const hash of PAGE_HASHES) {
    if (trimmed.endsWith(hash)) {
      return hash;
    }
  }
  return null;
}

function isPageHashLink(anchor: Element): anchor is HTMLAnchorElement {
  if (!(anchor instanceof HTMLAnchorElement)) {
    return false;
  }
  const href = anchor.getAttribute("href");
  if (!href) {
    return false;
  }
  return normalizeHash(href) != null;
}

function getHashScrollTop(
  hash: string,
  lenis: LenisLike | null,
): number | null {
  const id = hash.slice(1);
  const target = document.getElementById(id);
  if (!target) {
    return null;
  }

  const currentScroll = lenis?.scroll ?? window.scrollY;
  return target.getBoundingClientRect().top + currentScroll + SCROLL_OFFSET;
}

function scrollToHash(hash: string, lenis: LenisLike | null) {
  const top = getHashScrollTop(hash, lenis);
  if (top == null) {
    return;
  }

  const instant = prefersReducedMotion();

  if (lenis) {
    lenis.scrollTo(top, {
      duration: instant ? 0 : SCROLL_DURATION,
      force: true,
    });
    return;
  }

  window.scrollTo({
    top,
    behavior: instant ? "auto" : "smooth",
  });
}

function clearHash() {
  const clean = `${window.location.pathname}${window.location.search}`;
  if (window.location.hash) {
    window.history.replaceState(window.history.state, "", clean);
  }
}

/**
 * Hash scroll do `#formularz` / `#opinie`.
 * Mobile: natywny scroll. Desktop: Lenis przez event (bez importu Lenisa tu).
 */
export function SalaHashScrollFix() {
  const lenisRef = useRef<LenisLike | null>(null);

  useEffect(() => {
    const onLenisReady = (event: Event) => {
      lenisRef.current = (event as CustomEvent<LenisLike>).detail ?? null;
    };
    window.addEventListener("monicatering:lenis-ready", onLenisReady);

    const getLenis = () => lenisRef.current;

    const handleClick = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest("a");
      if (!anchor || !isPageHashLink(anchor)) {
        return;
      }

      const href = anchor.getAttribute("href");
      const hash = href ? normalizeHash(href) : null;
      if (!hash) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      scrollToHash(hash, getLenis());
      clearHash();
    };

    document.addEventListener("click", handleClick, true);

    const scrollIfHash = () => {
      const hash = window.location.hash;
      if (!PAGE_HASHES.includes(hash as (typeof PAGE_HASHES)[number])) {
        return;
      }

      let attempts = 0;
      const tryScroll = () => {
        const top = getHashScrollTop(hash, getLenis());
        if (top == null && attempts < 24) {
          attempts += 1;
          requestAnimationFrame(tryScroll);
          return;
        }
        if (top != null) {
          scrollToHash(hash, getLenis());
        }
        clearHash();
      };

      requestAnimationFrame(tryScroll);
    };

    scrollIfHash();
    window.addEventListener("hashchange", scrollIfHash);

    return () => {
      window.removeEventListener("monicatering:lenis-ready", onLenisReady);
      document.removeEventListener("click", handleClick, true);
      window.removeEventListener("hashchange", scrollIfHash);
    };
  }, []);

  return null;
}
